import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Card, IconButton } from "@mui/material";
import { Edit, Delete as DeleteIcon } from "components/icons";
import { handleAxiosError } from "app/services/app.error.handler";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { ConfirmDialog } from "../ConfirmDialog";
import { PaginatedDataGrid } from "app/components/common/PaginatedDataGrid";
import { DEFAULT_TABLE_PAGE_SIZE } from "AppConstants";
import { getHolidayTypeLabel } from "utils/holidayTypeLabel";
import AddHolidayDialog from "./AddHolidayDialog";
import {
  loadHolidayCalendarsFromBackend,
  loadCalendarHolidaysPageFromBackend,
  deleteHolidayFromBackend,
} from "app/services/holiday.service";

const DEFAULT_CALENDAR_NAME = "Default Company Calendar";

// Frontend grid field -> backend sortBy column (allowlisted server-side).
const SORT_FIELD_MAP = {
  id: "id",
  holidayDate: "holidayDate",
  name: "name",
};

const DEFAULT_SORT_BY = "holidayDate";
const DEFAULT_SORT_DIRECTION = "ASC";

// Holidays are scoped to a calendar (resolved on mount), so this grid keeps its own
// pagination/sort/search state rather than using the Redux useServerPaginatedGrid hook.
const AllHolidaysView = () => {
  const [open, setOpen] = useState(false);
  const [editObject, setEditObject] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [calendarId, setCalendarId] = useState(null);
  const [noCalendar, setNoCalendar] = useState(false);

  const [rows, setRows] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);
  const [sortDirection, setSortDirection] = useState(DEFAULT_SORT_DIRECTION);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Guard against out-of-order responses on rapid page/sort/search changes.
  const requestSeqRef = useRef(0);

  const fetchPage = useCallback(
    (calId, overrides = {}) => {
      if (!calId) return;
      const params = {
        page: overrides.page ?? page,
        pageSize: overrides.pageSize ?? pageSize,
        sortBy: overrides.sortBy ?? sortBy,
        sortDirection: overrides.sortDirection ?? sortDirection,
        searchQuery: overrides.searchQuery ?? searchQuery,
      };
      const seq = ++requestSeqRef.current;
      setIsLoading(true);
      loadCalendarHolidaysPageFromBackend(calId, params)
        .then((response) => {
          if (seq !== requestSeqRef.current) return;
          const body = response.data || {};
          setRows(Array.isArray(body.content) ? body.content : []);
          setTotalElements(typeof body.totalElements === "number" ? body.totalElements : 0);
          setPage(typeof body.page === "number" ? body.page : params.page);
          setPageSize(typeof body.pageSize === "number" ? body.pageSize : params.pageSize);
          setIsLoading(false);
        })
        .catch((error) => {
          if (seq !== requestSeqRef.current) return;
          setRows([]);
          setTotalElements(0);
          setIsLoading(false);
          handleAxiosError(error);
        });
    },
    [page, pageSize, sortBy, sortDirection, searchQuery]
  );

  useEffect(() => {
    loadHolidayCalendarsFromBackend()
      .then((response) => {
        const calendars = Array.isArray(response.data) ? response.data : [];
        if (calendars.length === 0) {
          setNoCalendar(true);
          return;
        }
        const defaultCal = calendars.find((c) => c.name === DEFAULT_CALENDAR_NAME) || calendars[0];
        setCalendarId(defaultCal.id);
        fetchPage(defaultCal.id);
      })
      .catch((error) => {
        setNoCalendar(true);
        handleAxiosError(error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePaginationModelChange = (model) => {
    if (model.page === page && model.pageSize === pageSize) return;
    setPage(model.page);
    setPageSize(model.pageSize);
    fetchPage(calendarId, { page: model.page, pageSize: model.pageSize });
  };

  const handleSortModelChange = ({ sortBy: nextSortBy, sortDirection: nextSortDirection }) => {
    if (nextSortBy === sortBy && nextSortDirection === sortDirection) return;
    setSortBy(nextSortBy);
    setSortDirection(nextSortDirection);
    setPage(0);
    fetchPage(calendarId, { page: 0, sortBy: nextSortBy, sortDirection: nextSortDirection });
  };

  const handleSearchChange = (nextQuery) => {
    const normalized = (nextQuery ?? "").trim();
    if (normalized === searchQuery) return;
    setSearchQuery(normalized);
    setPage(0);
    fetchPage(calendarId, { page: 0, searchQuery: normalized });
  };

  const refreshHolidays = () => fetchPage(calendarId);

  const editEventHandler = (data) => {
    setOpen(true);
    setEditObject(data);
  };

  const confirmDeleteDialogHandler = (data) => {
    setConfirmationMessage(`Are you sure you want to delete this Holiday`);
    setDeleteId(data.id);
    setConfirmOpen(true);
  };

  const deleteHandler = async () => {
    await deleteHolidayFromBackend(calendarId, deleteId)
      .then(() => refreshHolidays())
      .catch((error) => handleAxiosError(error));
  };

  const columns = [
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      cellClassName: "actions",
      getActions: (obj) => [
        <IconButton aria-label="Edit" onClick={() => editEventHandler(obj.row)} color="inherit">
          <Edit />
        </IconButton>,
        <IconButton aria-label="Delete" onClick={() => confirmDeleteDialogHandler(obj.row)} color="inherit">
          <DeleteIcon />
        </IconButton>,
      ],
    },
    { field: "id", headerName: "Id", width: 80 },
    { field: "holidayDate", headerName: "Date", width: 160 },
    { field: "name", headerName: "Name", width: 250 },
    // Not sortable: holidayType is absent from the backend sortBy allowlist, so a sort
    // request on it would be rejected as a 400.
    {
      field: "holidayType",
      headerName: "Type",
      width: 120,
      sortable: false,
      valueGetter: (value, row) => getHolidayTypeLabel(row.holidayType),
    },
    { field: "description", headerName: "Description", width: 350, sortable: false },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={1}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
        >
          <MDTypography variant="h6" color="white">
            Holidays
          </MDTypography>
        </MDBox>
        {noCalendar ? (
          <MDBox mt={2} px={2} py={1}>
            <MDTypography variant="body2" color="text">
              No holiday calendar configured. Please set up a holiday calendar to manage holidays.
            </MDTypography>
          </MDBox>
        ) : (
          <MDBox height={500} mb={0} sx={{ overflowX: "auto" }}>
            <PaginatedDataGrid
              rows={rows}
              columns={columns}
              totalElements={totalElements}
              page={page}
              pageSize={pageSize}
              sortBy={sortBy}
              sortDirection={sortDirection}
              searchQuery={searchQuery}
              isLoading={isLoading}
              sortFieldMap={SORT_FIELD_MAP}
              defaultSortBy={DEFAULT_SORT_BY}
              defaultSortDirection={DEFAULT_SORT_DIRECTION}
              onPaginationModelChange={handlePaginationModelChange}
              onSortModelChange={handleSortModelChange}
              onSearchChange={handleSearchChange}
              onAdd={() => {
                setEditObject(null);
                setOpen(true);
              }}
              addLabel="Add Holiday"
              columnVisibilityModel={{ id: false }}
            />
          </MDBox>
        )}
      </Card>
      <AddHolidayDialog
        open={open}
        setOpen={setOpen}
        refreshActivitiesDataHandler={refreshHolidays}
        editObject={editObject}
        calendarId={calendarId}
      />
      <ConfirmDialog
        title={"Delete This Holiday?"}
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={deleteHandler}
        yesButtonLabel="Delete"
        noButtonLabel="Cancel"
      >
        {confirmationMessage}
      </ConfirmDialog>
    </Box>
  );
};

export default AllHolidaysView;
